import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getIngestionJobs, getIngestionUploadUrl, processIngestionJob, submitIngestionJob, runIngestion, generateRegulationViews, generateSingleView, getViewCoverage, deleteIngestionJob, stopEcsTask, getBackfillStatus } from '@/api/adminService'

export function useIngestionJobs() {
  return useQuery({
    queryKey: ['admin', 'ingestion', 'jobs'],
    queryFn: () => getIngestionJobs(20),
    refetchInterval: 15000, // poll every 15s to pick up status changes
  })
}

export function useRunIngestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: runIngestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'jobs'] })
    },
  })
}

export function useUploadUrl() {
  return useMutation({
    mutationFn: (filename: string) => getIngestionUploadUrl(filename),
  })
}

export function useSubmitJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (manifest_key: string) => submitIngestionJob(manifest_key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'jobs'] })
    },
  })
}

export function useProcessJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (manifest_key: string) => processIngestionJob(manifest_key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'jobs'] })
    },
  })
}

export function useGenerateViews() {
  return useMutation({
    mutationFn: (params: { dry_run?: boolean; pub_authority_uuid?: string; missing_only?: boolean }) => generateRegulationViews(params),
  })
}

export function useGenerateSingleView() {
  return useMutation({
    mutationFn: (legislationUuid: string) => generateSingleView(legislationUuid),
  })
}

export function useViewCoverage() {
  return useQuery({
    queryKey: ['admin', 'ingestion', 'view-coverage'],
    queryFn: getViewCoverage,
  })
}

export function useBackfillStatus() {
  return useQuery({
    queryKey: ['admin', 'ingestion', 'backfill-status'],
    queryFn: getBackfillStatus,
    refetchInterval: 15000,
  })
}

export function useStopTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (task_arn: string) => stopEcsTask(task_arn),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'backfill-status'] })
    },
  })
}

export function useDeleteJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ run_id, manifest_key }: { run_id: string; manifest_key: string }) =>
      deleteIngestionJob(run_id, manifest_key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'jobs'] })
    },
  })
}
